import React, { useState } from 'react';
import { View } from 'react-native';
import {
  CURRENT_USER,
  decisionResults,
  contributionTypes,
  statuses,
  newId,
  type Venture,
  type Command,
  type Commitment,
  type Decision,
  commandSchema,
} from '@ventureroom/core';
import { useApp, useData, useTheme } from './provider';
import {
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Field,
  FormSheet,
  FormValues,
  Heading,
  Metric,
  Row,
  Segments,
  Tag,
  dateLabel,
  timeLabel,
} from './components';
const localDateTime = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
const today = () => localDateTime(new Date()).slice(0, 10);
const later = (days = 3) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localDateTime(d).slice(0, 16);
};
function iso(value: string | string[] | number | undefined) {
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime()))
    throw Error('Enter a valid date and time, for example 2026-10-02T17:00.');
  return d.toISOString();
}
function selectedPeople(v: FormValues, key: string, venture: Venture, names: string[]) {
  return (Array.isArray(v[key]) ? v[key] : [])
    .map((name) => venture.members[names.indexOf(name)]!)
    .filter(Boolean);
}
export function DecisionPanel({ venture }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const t = useTheme();
  const [create, setCreate] = useState(false),
    [history, setHistory] = useState('Open'),
    [comment, setComment] = useState<Decision | null>(null);
  const names = venture.members.map((id) => data.profiles.find((p) => p.id === id)!.name);
  const decisions = data.decisions
    .filter((d) => d.ventureId === venture.id)
    .filter((d) =>
      history === 'History'
        ? d.closedAt || d.deadline <= new Date().toISOString()
        : !d.closedAt && d.deadline > new Date().toISOString(),
    );
  return (
    <Column gap={20}>
      <Row between wrap>
        <Heading size={22}>Decisions</Heading>
        <Button onPress={() => setCreate(true)}>+ New decision</Button>
      </Row>
      <Segments items={['Open', 'History']} value={history} onChange={setHistory} />
      {decisions.map((d) => {
        const result = decisionResults(d);
        const closed = !!d.closedAt || d.deadline <= new Date().toISOString();
        return (
          <Card key={d.id}>
            <Row between>
              <Tag>{closed ? 'Voting closed' : 'Your perspective matters'}</Tag>
              <Copy muted size={12}>
                {closed ? 'Closed' : 'Closes'} {dateLabel(d.deadline)}
              </Copy>
            </Row>
            <Heading size={22}>{d.title}</Heading>
            <Copy muted>{d.context}</Copy>
            {d.options.map((option, index) => (
              <View key={option} style={{ gap: 7 }}>
                <Row between>
                  <Copy bold size={14}>
                    {option}
                  </Copy>
                  <Copy muted size={12}>
                    {result.counts[index]} vote{result.counts[index] === 1 ? '' : 's'}
                  </Copy>
                </Row>
                <View style={{ height: 5, borderRadius: 6, backgroundColor: t.surfaceAlt }}>
                  <View
                    style={{
                      width: `${result.total ? ((result.counts[index] ?? 0) / result.total) * 100 : 0}%`,
                      height: 5,
                      borderRadius: 6,
                      backgroundColor: t.accent,
                    }}
                  />
                </View>
                {!closed && d.participants.includes(CURRENT_USER) && (
                  <Button
                    kind={d.votes[CURRENT_USER] === index ? 'primary' : 'secondary'}
                    disabled={app.busy}
                    onPress={() =>
                      void app.act(
                        { type: 'vote', id: d.id, option: index },
                        'Your vote is recorded',
                      )
                    }
                  >
                    {d.votes[CURRENT_USER] === index ? '✓ Your vote' : `Vote: ${option}`}
                  </Button>
                )}
              </View>
            ))}
            <Copy muted size={12}>
              {result.total} of {d.participants.length} people have voted. One vote per person; you
              can change it before closing.
            </Copy>
            {closed && (
              <Copy bold>
                {result.total
                  ? `${result.tied ? 'Tied result' : 'Leading choice'}: ${result.leaders.join(' / ')}`
                  : 'No votes were recorded.'}
              </Copy>
            )}
            {d.comments.map((c, i) => (
              <Copy key={i} size={13}>
                {data.profiles.find((p) => p.id === c.author)?.name}: {c.text}
              </Copy>
            ))}
            <Row wrap>
              <Button kind="quiet" onPress={() => setComment(d)}>
                Add a thought
              </Button>
              {!closed && d.author === CURRENT_USER && (
                <Button
                  kind="secondary"
                  onPress={() =>
                    void app.act(
                      { type: 'closeDecision', id: d.id },
                      'Decision closed and saved in history',
                    )
                  }
                >
                  Close decision
                </Button>
              )}
            </Row>
          </Card>
        );
      })}
      {!decisions.length && (
        <Empty
          title={
            history === 'Open'
              ? 'Make room for everyone’s perspective'
              : 'A record of the calls you made'
          }
          detail={
            history === 'Open'
              ? 'Create a decision when the team needs to choose a direction.'
              : 'Closed decisions stay here, including the votes and context.'
          }
        />
      )}
      {create && (
        <FormSheet
          title="Create a decision"
          fields={[
            { key: 'title', label: 'What do you need to decide?', required: true },
            { key: 'context', label: 'Give the team some context', type: 'long' },
            {
              key: 'options',
              label: 'Options — one per line',
              type: 'long',
              required: true,
              hint: 'Option one\nOption two',
            },
            {
              key: 'deadline',
              label: 'Deadline (local date and time)',
              hint: 'YYYY-MM-DDTHH:mm',
              required: true,
            },
            {
              key: 'participants',
              label: 'Participants',
              type: 'multi',
              options: names,
              required: true,
            },
          ]}
          initial={{ title: '', context: '', options: '', deadline: later(), participants: names }}
          onClose={() => setCreate(false)}
          submit="Open decision"
          onSave={(v) =>
            app.act(
              {
                type: 'decision',
                decision: {
                  id: newId('decision'),
                  ventureId: venture.id,
                  author: CURRENT_USER,
                  title: String(v.title),
                  context: String(v.context),
                  options: String(v.options)
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                  deadline: iso(v.deadline),
                  participants: selectedPeople(v, 'participants', venture, names),
                  votes: {},
                  comments: [],
                  createdAt: new Date().toISOString(),
                },
              },
              'Decision opened',
            )
          }
        />
      )}
      {comment && (
        <FormSheet
          title="Add your perspective"
          fields={[{ key: 'text', label: 'Your thought', type: 'long', required: true }]}
          initial={{ text: '' }}
          onClose={() => setComment(null)}
          onSave={(v) =>
            app.act(
              { type: 'decisionComment', id: comment.id, text: String(v.text) },
              'Thought added',
            )
          }
        />
      )}
    </Column>
  );
}
export function CommitmentPanel({ venture }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const t = useTheme();
  const [create, setCreate] = useState(false),
    [filter, setFilter] = useState('All'),
    [comment, setComment] = useState<Commitment | null>(null);
  const items = data.commitments.filter(
    (c) => c.ventureId === venture.id && (filter === 'All' || c.status === filter),
  );
  const names = venture.members.map((id) => data.profiles.find((p) => p.id === id)!.name);
  return (
    <Column gap={20}>
      <Row between wrap>
        <Heading size={22}>Commitments</Heading>
        <Button onPress={() => setCreate(true)}>+ New commitment</Button>
      </Row>
      <Segments items={['All', ...statuses]} value={filter} onChange={setFilter} />
      {items.map((c) => (
        <Card key={c.id}>
          <Row between wrap>
            <Tag accent={c.status === 'In Progress'}>
              {c.status === 'Completed' ? '✓ ' : ''}
              {c.status}
            </Tag>
            <Copy
              color={c.dueDate < today() && c.status !== 'Completed' ? t.warning : t.muted}
              size={12}
            >
              {c.dueDate < today() && c.status !== 'Completed' ? 'Past due · ' : ''}
              {dateLabel(c.dueDate)}
            </Copy>
          </Row>
          <Heading size={20}>{c.title}</Heading>
          <Copy muted>{c.description}</Copy>
          <Row wrap>
            <Tag>{data.profiles.find((p) => p.id === c.owner)?.name}</Tag>
            <Tag>{c.priority} priority</Tag>
            
          </Row>
          <Copy muted size={12}>
            Milestone: {c.milestone}
          </Copy>
          <Segments
            items={[...statuses]}
            value={c.status}
            onChange={(status) =>
              void app.act(
                { type: 'commitmentStatus', id: c.id, status: status as Commitment['status'] },
                status === 'Completed' ? 'A little progress, recorded.' : 'Commitment updated',
              )
            }
          />
          {!!c.completedAt && (
            <Copy color={t.success} size={12}>
              Completed {dateLabel(c.completedAt)} · recorded on platform
            </Copy>
          )}
          {c.comments.map((m, i) => (
            <Copy key={i} size={13}>
              {data.profiles.find((p) => p.id === m.author)?.name}: {m.text}
            </Copy>
          ))}
          <Button kind="quiet" onPress={() => setComment(c)}>
            Add an update
          </Button>
        </Card>
      ))}
      {!items.length && (
        <Empty
          title="Room for the next small step"
          detail="Create a commitment with a clear owner and a realistic date."
        />
      )}
      {create && (
        <FormSheet
          title="Make a commitment"
          fields={[
            { key: 'title', label: 'What will get done?', required: true },
            { key: 'description', label: 'What does done look like?', type: 'long' },
            { key: 'owner', label: 'Owner', type: 'select', options: names },
            { key: 'dueDate', label: 'Due date', hint: 'YYYY-MM-DD', required: true },
            {
              key: 'priority',
              label: 'Priority',
              type: 'select',
              options: ['Low', 'Medium', 'High'],
            },
            { key: 'status', label: 'Status', type: 'select', options: [...statuses] },
            
            { key: 'milestone', label: 'Linked milestone', required: true },
          ]}
          initial={{
            title: '',
            description: '',
            owner: data.profiles.find((p) => p.id === CURRENT_USER)!.name,
            dueDate: later().slice(0, 10),
            priority: 'Medium',
            status: 'In Progress',
            effort: 2,
            milestone: venture.milestone,
          }}
          onClose={() => setCreate(false)}
          submit="Create commitment"
          onSave={(v) =>
            app.act(
              commandSchema.parse({
                type: 'commitment',
                commitment: {
                  ...v,
                  id: newId('commitment'),
                  ventureId: venture.id,
                  owner: venture.members[names.indexOf(String(v.owner))],
                  comments: [],
                  createdAt: new Date().toISOString(),
                },
              }),
              'Commitment recorded',
            )
          }
        />
      )}
      {comment && (
        <FormSheet
          title="Share a work update"
          fields={[{ key: 'text', label: 'Update or obstacle', type: 'long', required: true }]}
          initial={{ text: '' }}
          onClose={() => setComment(null)}
          onSave={(v) =>
            app.act(
              { type: 'commitmentComment', id: comment.id, text: String(v.text) },
              'Update added',
            )
          }
        />
      )}
    </Column>
  );
}
export function CalendarPanel({ venture }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const [create, setCreate] = useState(false),
    [tab, setTab] = useState('Upcoming');
  const names = venture.members.map((id) => data.profiles.find((p) => p.id === id)!.name);
  const events = data.events
    .filter(
      (e) =>
        e.ventureId === venture.id &&
        (tab === 'Past'
          ? e.startsAt < new Date().toISOString()
          : e.startsAt >= new Date().toISOString()),
    )
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  return (
    <Column gap={20}>
      <Row between wrap>
        <Heading size={22}>Calendar</Heading>
        <Button onPress={() => setCreate(true)}>+ Add event</Button>
      </Row>
      <Segments items={['Upcoming', 'Past']} value={tab} onChange={setTab} />
      {events.map((e) => (
        <Card key={e.id}>
          <Row wrap>
            <Column gap={2} style={{ minWidth: 75 }}>
              <Heading size={25}>{dateLabel(e.startsAt)}</Heading>
              <Copy muted>{timeLabel(e.startsAt)}</Copy>
            </Column>
            <Column gap={8} style={{ flex: 1, minWidth: 190 }}>
              <Tag>{e.type}</Tag>
              <Heading size={20}>{e.title}</Heading>
              <Copy muted>{e.details}</Copy>
              <Copy muted size={12}>
                {e.participants
                  .map((id) => data.profiles.find((p) => p.id === id)?.name)
                  .join(' · ')}
              </Copy>
            </Column>
          </Row>
        </Card>
      ))}
      {!events.length && (
        <Empty
          title="Make time for what matters"
          detail="Add a team check-in, a deadline, or your next milestone."
        />
      )}
      {create && (
        <FormSheet
          title="Make a little time"
          fields={[
            { key: 'title', label: 'Event title', required: true },
            {
              key: 'type',
              label: 'Kind of event',
              type: 'select',
              options: ['Meeting', 'Milestone', 'Deadline', 'Launch', 'Custom event'],
            },
            {
              key: 'startsAt',
              label: 'Local date and time',
              hint: 'YYYY-MM-DDTHH:mm',
              required: true,
            },
            { key: 'details', label: 'Details & agenda', type: 'long' },
            {
              key: 'participants',
              label: 'Who’s joining?',
              type: 'multi',
              options: names,
              required: true,
            },
          ]}
          initial={{
            title: '',
            type: 'Meeting',
            startsAt: later(),
            details: '',
            participants: names,
          }}
          onClose={() => setCreate(false)}
          submit="Add to calendar"
          onSave={(v) =>
            app.act(
              {
                type: 'event',
                event: {
                  ...v,
                  id: newId('event'),
                  ventureId: venture.id,
                  startsAt: iso(v.startsAt),
                  participants: selectedPeople(v, 'participants', venture, names),
                },
              } as Command,
              'Calendar event added',
            )
          }
        />
      )}
      <Copy muted size={12}>
        Times are displayed in your device’s timezone and stored in UTC. External calendar sync is
        not enabled.
      </Copy>
    </Column>
  );
}
export function ContributionPanel({ venture }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const [create, setCreate] = useState(false);
  const items = data.contributions
    .filter((c) => c.ventureId === venture.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const commitments = data.commitments.filter((c) => c.ventureId === venture.id);
  const fields: Field[] = [
    { key: 'type', label: 'Contribution type', type: 'select', options: [...contributionTypes] },
    { key: 'title', label: 'What did you contribute?', required: true },
    { key: 'description', label: 'A little context', type: 'long' },
    { key: 'quantity', label: 'Quantity (hours for Time, otherwise units)', type: 'number' },
    { key: 'expense', label: 'Historical expense in GBP (Expense paid only)', type: 'number' },
    { key: 'date', label: 'Date contributed (UTC)', hint: 'YYYY-MM-DD', required: true },
    {
      key: 'linked',
      label: 'Related commitment',
      type: 'select',
      options: ['None', ...commitments.map((c) => c.title)],
    },
    {
      key: 'evidence',
      label: 'Evidence or a note',
      type: 'long',
      hint: 'Describe what the team can refer back to.',
    },
  ];
  return (
    <Column gap={20}>
      <Row between wrap>
        <Heading size={22}>Contributions</Heading>
        <Button onPress={() => setCreate(true)}>+ Record contribution</Button>
      </Row>
      <Card>
        <Row wrap>
          <Metric value={items.length} label="contributions" />
          <Metric
            value={items
              .filter((c) => c.type === 'Time')
              .reduce((n, c) => n + (c.quantity ?? 0), 0)}
            label="hours recorded"
          />
        </Row>
        <Copy muted size={12}>
          A private, append-oriented history. Expenses record something already paid; they do not
          make a payment or create ownership rights.
        </Copy>
      </Card>
      {items.map((c) => (
        <Card key={c.id}>
          <Row between wrap>
            <Tag>{c.type}</Tag>
            <Copy muted size={12}>
              {dateLabel(c.date)}
            </Copy>
          </Row>
          <Heading size={20}>{c.title}</Heading>
          <Copy muted>{c.description}</Copy>
          <Row wrap>
            <Copy bold size={13}>
              {data.profiles.find((p) => p.id === c.contributor)?.name}
            </Copy>
            {c.quantity !== undefined && (
              <Tag>
                {c.quantity} {c.type === 'Time' ? 'hours' : 'units'}
              </Tag>
            )}
            {c.expenseMinor !== undefined && (
              <Tag>£{(c.expenseMinor / 100).toFixed(2)} already paid</Tag>
            )}
          </Row>
          {!!c.evidence && (
            <Copy muted size={13}>
              Evidence / note: {c.evidence}
            </Copy>
          )}
          {!!c.commitmentId && (
            <Copy muted size={12}>
              Related: {commitments.find((t) => t.id === c.commitmentId)?.title}
            </Copy>
          )}
          <Copy muted size={11}>
            Recorded {dateLabel(c.createdAt)} · historical entry
          </Copy>
        </Card>
      ))}
      {!items.length && (
        <Empty
          title="The first entry starts a story"
          detail="Record work delivered, time, research, an introduction, or a past expense."
        />
      )}
      {create && (
        <FormSheet
          title="Record a contribution"
          fields={fields}
          initial={{
            type: 'Work delivered',
            title: '',
            description: '',
            quantity: '',
            expense: '',
            date: new Date().toISOString().slice(0, 10),
            linked: 'None',
            evidence: '',
          }}
          onClose={() => setCreate(false)}
          submit="Add historical record"
          onSave={(v) => {
            if (v.expense !== '' && v.type !== 'Expense paid')
              throw Error('Only Expense paid records can include an amount.');
            return app.act(
              {
                type: 'contribution',
                contribution: {
                  id: newId('contribution'),
                  ventureId: venture.id,
                  contributor: CURRENT_USER,
                  type: v.type,
                  title: v.title,
                  description: v.description,
                  quantity: v.quantity === '' ? undefined : Number(v.quantity),
                  expenseMinor: v.expense === '' ? undefined : Math.round(Number(v.expense) * 100),
                  currency: 'GBP',
                  date: v.date,
                  commitmentId: commitments.find((c) => c.title === v.linked)?.id,
                  evidence: v.evidence,
                  createdAt: new Date().toISOString(),
                },
              } as Command,
              'Contribution added to history',
            );
          }}
        />
      )}
    </Column>
  );
}
